import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import {
  Container,
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Stack,
  Divider,
  useMediaQuery,
  useTheme,
  Paper,
  IconButton
} from "@mui/material";
import {
  SportsSoccer,
  SportsBasketball,
  SportsCricket,
  SportsHockey,
  ArrowForward,
  Brush,
  Palette,
  LocalOffer,
  CheckCircleOutline,
  ArrowBack,
  ArrowForward as ArrowForwardIcon
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import {
  categoriesForHomeRow1,
  categoriesForHomeRow2
} from "../utils/homeCategoryRows";

const apiUrl = process.env.REACT_APP_API_URL || "/api";

const CATEGORIES = [
  {
    id: 1,
    name: "Football",
    slug: "football",
    icon: <SportsSoccer sx={{ fontSize: 40 }} />,
    description: "Custom football kits and jerseys",
    color: "#000000",
    image: "/assets/home/category-football.jpg"
  },
  {
    id: 2,
    name: "Basketball",
    slug: "basketball",
    icon: <SportsBasketball sx={{ fontSize: 40 }} />,
    description: "Professional basketball uniforms",
    color: "#000000",
    image: "/assets/home/category-basketball.jpg"
  },
  {
    id: 3,
    name: "Cricket",
    slug: "cricket",
    icon: <SportsCricket sx={{ fontSize: 40 }} />,
    description: "Cricket team kits and jerseys",
    color: "#000000",
    image: "/assets/home/category-cricket.jpg"
  },
  {
    id: 4,
    name: "Hockey",
    slug: "hockey",
    icon: <SportsHockey sx={{ fontSize: 40 }} />,
    description: "Hockey team uniforms",
    color: "#000000",
    image: "/assets/home/category-hockey.jpg"
  }
];

const CLIENT_SLIDES = [
  {
    id: 1,
    heading: "Nuneaton Town FC",
    description: "Nuneaton Town Football Club #BackingTheBoro",
    image: "/assets/home/slide-1.jpg"
  },
  {
    id: 2,
    heading: "Birmingham Blaze FC",
    description: "Elite performance kits tailored for Birmingham Blaze.",
    image: "/assets/home/slide-2.jpg"
  },
  {
    id: 3,
    heading: "WSCFC",
    description: "Wolverhampton Sporting Community – bespoke kits built to win.",
    image:
      "https://images.unsplash.com/photo-1518091043644-c1d4457512c6?auto=format&fit=crop&w=1200&q=80"
  },
  {
    id: 4,
    heading: "FC Clacton",
    description: "Game-ready designs for the home of The Seasiders.",
    image: "/assets/home/slide-4.jpg"
  }
];

const WHY_US_POINTS = [
  "4 week turnaround on playing kit and leisurewear",
  "Men's and Women's fits available",
  "Any design is possible on playing kit",
  "Minimum orders of 10 per product",
  "Full 'on-court' and 'off-court' product ranges",
  "Products tested on Elite Athletes"
];

function Home() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeWordIndex, setActiveWordIndex] = useState(0);
  const [activeSlide, setActiveSlide] = useState(0);
  const categoriesRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const rotatingWords = useMemo(
    () => ["Innovative", "Bespoke", "Quality", "Reliable"],
    []
  );

  useEffect(() => {
    async function fetchCategories() {
      setLoading(true);
      try {
        const response = await axios.get(`${apiUrl}/categories`);
        setCategories(response.data);
      } catch (error) {
        setCategories(CATEGORIES);
      } finally {
        setLoading(false);
      }
    }

    fetchCategories();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveWordIndex((prev) => (prev + 1) % rotatingWords.length);
    }, 2500);

    return () => clearInterval(interval);
  }, [rotatingWords.length]);

  useEffect(() => {
    const slideTimer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % CLIENT_SLIDES.length);
    }, 5000);

    return () => clearInterval(slideTimer);
  }, []);

  const handlePrevSlide = () => {
    setActiveSlide((prev) => (prev === 0 ? CLIENT_SLIDES.length - 1 : prev - 1));
  };

  const handleNextSlide = () => {
    setActiveSlide((prev) => (prev + 1) % CLIENT_SLIDES.length);
  };

  const currentSlide = CLIENT_SLIDES[activeSlide];

  const handleScrollToCategories = () => {
    if (categoriesRef.current) {
      categoriesRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const renderHeroSection = () => (
    <Box
      sx={{
        position: "relative",
        backgroundImage:
          "linear-gradient(rgba(0,0,0,0.75), rgba(0,0,0,0.6)), url('/assets/home/JS68201971.avif')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        width: "100%",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        color: "white",
        py: { xs: 10, md: 12 },
        mb: 8,
        "&::after": {
          content: '""',
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: { xs: 72, md: 100 },
          background:
            "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.35) 100%)",
          pointerEvents: "none"
        }
      }}
    >
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Stack spacing={4} alignItems={isMobile ? "center" : "flex-start"}>
          <Typography
            variant="h2"
            component="h1"
            sx={{
              textAlign: isMobile ? "center" : "left",
              fontWeight: 700,
              fontSize: { xs: "2.5rem", md: "3.5rem" },
              lineHeight: 1.1
            }}
          >
            <Box component="span" sx={{ color: "secondary.main" }}>
              {rotatingWords[activeWordIndex]}
            </Box>
            <br />
            Teamwear Solutions
          </Typography>
          <Typography
            variant="h5"
            component="h2"
            sx={{
              textAlign: isMobile ? "center" : "left",
              maxWidth: 580,
              opacity: 0.9
            }}
          >
            The next generation of teamwear is here. Build, customise, and
            deliver performance kits designed for champions.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              endIcon={<ArrowForward />}
              sx={{
                fontWeight: 600,
                px: 4,
                borderRadius: 2
              }}
              onClick={handleScrollToCategories}
            >
              Start Designing
            </Button>
            <Button
              component={Link}
              to="/contact"
              variant="outlined"
              color="inherit"
              size="large"
              sx={{
                fontWeight: 600,
                px: 4,
                borderRadius: 2,
                borderColor: "rgba(255,255,255,0.6)",
                color: "rgba(255,255,255,0.9)",
                '&:hover': {
                  borderColor: "white",
                  backgroundColor: "rgba(255,255,255,0.12)"
                }
              }}
            >
              Talk to our team
            </Button>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} flexWrap="wrap">
            <Chip icon={<Brush />} label="Free Customisation" color="secondary" />
            <Chip icon={<Palette />} label="Custom colours" color="secondary" />
            <Chip icon={<LocalOffer />} label="Team Orders" color="secondary" />
          </Stack>
        </Stack>
      </Container>
    </Box>
  );

  const renderFeatureHighlight = () => (
    <Container maxWidth="md" sx={{ mb: 8 }}>
      <Paper
        elevation={3}
        sx={{
          p: { xs: 4, md: 6 },
          textAlign: "center",
          borderRadius: 3,
          background: "linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)"
        }}
      >
        <Typography variant="h4" component="h2" sx={{ fontWeight: 700, mb: 2 }}>
          More than kit — lasting partnerships
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          At Zaror we offer more than kit for our partner teams. We work with
          clubs across the UK and pride ourselves on relationships built for the
          long term.
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 720, mx: "auto" }}>
          Our 3D Kit Designer allows you and your team mates to choose your design,
          select your team colours and get an instant quote in 60 seconds!
        </Typography>
        <Divider sx={{ my: 3 }} />
        <Grid container spacing={3} justifyContent="center">
          {WHY_US_POINTS.slice(0, 3).map((point, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Stack spacing={1.5} alignItems="center">
                <CheckCircleOutline color="secondary" sx={{ fontSize: 42 }} />
                <Typography variant="subtitle1" fontWeight={600}>
                  {point}
                </Typography>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Paper>
    </Container>
  );

  const homeRow1Categories = categoriesForHomeRow1(
    categories,
    loading,
    CATEGORIES
  );
  const homeRow2Categories = categoriesForHomeRow2(categories, loading);

  const renderCategoryCard = (category) => (
          <Grid item xs={12} sm={6} md={3} key={category.id}>
            <Card
              elevation={4}
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                borderRadius: 3,
                '&:hover': {
                  transform: "translateY(-6px)",
                  boxShadow: 8
                }
              }}
            >
              <Box
                component={Link}
                to={`/${category.slug}`}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  textDecoration: "none",
                  color: "inherit"
                }}
              >
                <CardMedia
                  component="img"
                  image={category.image}
                  alt={category.name}
                  sx={{
                    height: { xs: 260, md: 320 },
                    objectFit: "contain",
                    backgroundColor: "#ffffff",
                    px: 2,
                    pt: 2
                  }}
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{
                      textAlign: "center",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      mb: 1.5
                    }}
                  >
                    {category.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center", mb: 3 }}
                  >
                    {category.description}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    sx={{
                      fontWeight: 600,
                      borderRadius: 2,
                      textTransform: "uppercase"
                    }}
                  >
                    Design Now
                  </Button>
                </CardContent>
              </Box>
            </Card>
          </Grid>
  );

  const renderCategories = () => (
    <Container ref={categoriesRef} maxWidth="lg" sx={{ mb: 10 }}>
      {/* First Row */}
      {homeRow1Categories.length > 0 && (
        <Box>
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            sx={{ textAlign: "center", mb: 4, fontWeight: 700 }}
          >
            Choose Your Sport
          </Typography>
          <Grid container spacing={4}>
            {homeRow1Categories.map((category) => renderCategoryCard(category))}
          </Grid>
        </Box>
      )}
    </Container>
  );

  const renderSecondRowCategories = () => (
    <Container maxWidth="lg" sx={{ mb: 10 }}>
      {homeRow2Categories.length > 0 && (
        <Box>
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            sx={{ textAlign: "center", mb: 4, fontWeight: 700 }}
          >
            More Sports
          </Typography>
          <Grid container spacing={4}>
            {homeRow2Categories.map((category) => renderCategoryCard(category))}
      </Grid>
        </Box>
      )}
    </Container>
  );

  const renderClientHighlights = () => (
    <Box sx={{ bgcolor: "grey.100", py: { xs: 8, md: 10 }, mb: 10 }}>
      <Container maxWidth="lg">
        <Grid container spacing={4} alignItems="stretch">
          <Grid item xs={12} md={6} sx={{ display: "flex", minWidth: 0 }}>
            <Box
              sx={{
                position: "relative",
                overflow: "hidden",
                width: "100%",
                flex: "1 1 0",
                minWidth: 0,
                alignSelf: "stretch",
                minHeight: { xs: 320, sm: 380, md: 440 },
                height: { md: "100%" },
                color: "white",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                p: 3,
                boxShadow: 6,
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  backgroundImage: `linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.45)), url(${currentSlide.image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  zIndex: 0,
                },
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, position: "relative", zIndex: 1 }}>
                {currentSlide.heading}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, position: "relative", zIndex: 1 }}>
                {currentSlide.description}
              </Typography>
              <Box
                sx={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  display: "flex",
                  gap: 1,
                  zIndex: 2,
                }}
              >
                <IconButton
                  onClick={handlePrevSlide}
                  size="small"
                  sx={{
                    bgcolor: "rgba(0,0,0,0.4)",
                    color: "white",
                    '&:hover': { bgcolor: "rgba(0,0,0,0.6)" }
                  }}
                  aria-label="Previous slide"
                >
                  <ArrowBack fontSize="small" />
                </IconButton>
                <IconButton
                  onClick={handleNextSlide}
                  size="small"
                  sx={{
                    bgcolor: "rgba(0,0,0,0.4)",
                    color: "white",
                    '&:hover': { bgcolor: "rgba(0,0,0,0.6)" }
                  }}
                  aria-label="Next slide"
                >
                  <ArrowForwardIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
            <Stack spacing={3}>
              <Typography variant="h4" component="h2" sx={{ fontWeight: 700 }}>
                Enquiry / design
              </Typography>
              <Typography variant="body1" color="text.secondary">
                From playing kit to leisurewear, our in-house team and kit builder help
                you get the right look — with the turnaround times and product ranges
                outlined alongside our partnership approach above.
              </Typography>
              <Typography variant="body1" color="text.secondary">
                <strong>Minimum orders:</strong> 10 per product. Men&apos;s and
                Women&apos;s fits available. Start in the designer or contact us for a
                bespoke brief.
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button
                  component={Link}
                  to="/contact"
                  variant="contained"
                  color="secondary"
                  endIcon={<ArrowForward />}
                  sx={{ fontWeight: 600, borderRadius: 2 }}
                >
                  Request a Quote
                </Button>
                <Button
                  component={Link}
                  to="/football"
                  variant="outlined"
                  color="primary"
                  sx={{ fontWeight: 600, borderRadius: 2 }}
                >
                  Launch Kit Designer
                </Button>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );

  const renderWhyUsSection = () => (
    <Container maxWidth="lg" sx={{ mb: 10 }}>
      <Grid container spacing={6} alignItems="stretch">
        <Grid item xs={12} md={6}>
          <Typography variant="h6" color="secondary" sx={{ mb: 1 }}>
            Why Zaror
          </Typography>
          <Typography variant="h4" component="h3" sx={{ fontWeight: 700, mb: 3 }}>
            Clubs, tours, schools & universities
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            At Zaror we offer more than just kit for our partner teams. We pride
            ourselves on building long-term relationships with clubs across the UK.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            We have an in-house design team that can work with you to create the
            perfect kit for your club, tour, school or university.
          </Typography>
          <Stack spacing={1.5}>
            {WHY_US_POINTS.map((point, index) => (
              <Stack direction="row" spacing={1.5} alignItems="center" key={index}>
                <CheckCircleOutline color="secondary" />
                <Typography variant="body1">{point}</Typography>
              </Stack>
            ))}
          </Stack>
        </Grid>
        <Grid item xs={12} md={6} sx={{ display: "flex" }}>
          <Box
            sx={{
              position: "relative",
              flex: 1,
              alignSelf: "stretch",
              width: "100%",
              minHeight: { xs: 320, sm: 380, md: 420 },
              height: { md: "100%" },
              overflow: "hidden",
              boxShadow: 6
            }}
          >
            <Box
              component="img"
              src="/assets/home/why-us.jpg"
              alt="Zaror Sports teamwear"
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center",
                display: "block"
              }}
            />
          </Box>
        </Grid>
      </Grid>
    </Container>
  );

  return (
    <Box sx={{ bgcolor: "background.default" }}>
      {renderHeroSection()}
      {renderFeatureHighlight()}
      {renderCategories()}
      {renderClientHighlights()}
      {renderSecondRowCategories()}
      {renderWhyUsSection()}
    </Box>
  );
}

export default Home;

